import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Phone, 
  MapPin,
  User,
  Mail,
  Calendar,
  Building
} from 'lucide-react';
import { formatCPF, formatPhone } from '../utils/validators';

import AddressCepField from '../components/anamnese/AddressCepField';
import { isAddressQuestion } from '../utils/anamneseFieldTypes';

const PublicAnamnesePage = () => {
  const { publicLinkId } = useParams();
  const [template, setTemplate] = useState(null);
  const [clinicInfo, setClinicInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patientData, setPatientData] = useState({
    name: '',
    email: '',
    phone: '',
    birth_date: ''
  });
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const totalQuestions = template?.questions?.length || 0;

  const answeredQuestions = template?.questions?.filter((question) => {
    const value = responses[question.id];

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return String(value || '').trim() !== '';
  }).length || 0;

  const progressPercent = totalQuestions
    ? Math.round((answeredQuestions / totalQuestions) * 100)
    : 0;

  const clinicLogoSrc = clinicInfo?.logo_data
    ? `data:image/jpeg;base64,${clinicInfo.logo_data}`
    : clinicInfo?.logo || null;

  const backendUrl =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8001'
    : '');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Buscar template
        const templateResponse = await fetch(`${backendUrl}/api/anamnese/templates/public/${publicLinkId}`);
        
        if (!templateResponse.ok) {
          throw new Error('Template não encontrado ou link inválido');
        }

        const templateData = await templateResponse.json();
        setTemplate(templateData);

        if (templateData.clinic_info) {
          setClinicInfo(templateData.clinic_info);
        }

        // Buscar informações da clínica
        try {
          const clinicResponse = await fetch(`${backendUrl}/api/clinic-settings`);
          
          if (clinicResponse.ok) {
            const clinicData = await clinicResponse.json();
            setClinicInfo(current => current || clinicData);
          }
        } catch (clinicError) {
          console.warn('Could not fetch clinic info:', clinicError);
        }

      } catch (error) {
        setError(error.message || 'Erro de conexão. Verifique sua internet e tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    if (publicLinkId) {
      fetchData();
    }
  }, [publicLinkId, backendUrl]);

  const handlePatientDataChange = (field, value) => {
    if (field === 'phone') {
      // Formatação de telefone
      const cleaned = value.replace(/\D/g, '');
      const formatted = cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      setPatientData(prev => ({
        ...prev,
        [field]: formatted
      }));
    } else {
      setPatientData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleResponseChange = (questionId, value, questionLabel = '') => {
    let formattedValue = value;
    
    // Formata CPF
    if (questionLabel.toLowerCase().includes('cpf') || questionLabel.toLowerCase().includes('c.p.f')) {
      formattedValue = formatCPF(value);
    }
    
    // Formata telefone / celular / WhatsApp
    if (
      questionLabel.toLowerCase().includes('celular') ||
      questionLabel.toLowerCase().includes('whatsapp') ||
      questionLabel.toLowerCase() === 'telefone' ||
      questionLabel.toLowerCase().startsWith('telefone ')
    ) {
      formattedValue = formatPhone(value);
    }
    
    setResponses(prev => ({
      ...prev,
      [questionId]: formattedValue
    }));
  };

  const validateForm = () => {
    if (!patientData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }

    // Verificar perguntas obrigatórias
    const requiredQuestions = template?.questions?.filter(q => q.is_required) || [];
    for (const question of requiredQuestions) {
      if (!responses[question.id] || responses[question.id].toString().trim() === '') {
        setError(`A pergunta "${question.question_text}" é obrigatória`);
        return false;
      }
    }

    setError(null);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Preparar dados no formato correto para o backend
      const answers = Object.entries(responses).map(([questionId, answer]) => {
        const question = template.questions.find(q => q.id === questionId);
        return {
          question_id: questionId,
          question_text: question?.question_text || '',
          answer: Array.isArray(answer) ? answer.join(', ') : answer.toString()
        };
      });

      // Extrair email, telefone e data de nascimento das respostas (se existirem)
      let extractedEmail = patientData.email;
      let extractedPhone = patientData.phone;
      let extractedBirthDate = patientData.birth_date;
      let extractedPhonePriority = extractedPhone ? 100 : 0;

      template.questions?.forEach((question) => {
        const answer = responses[question.id];
        const questionText = (question.question_text || question.label || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim();
        
        if (answer && (questionText.includes('email') || questionText.includes('e-mail'))) {
          extractedEmail = answer;
        }

        const isOtherPersonPhone =
          questionText.includes('emergencia') ||
          questionText.includes('medico') ||
          questionText.includes('acompanha voce') ||
          questionText.includes('dessa pessoa');
        const phonePriority =
          questionText.includes('whatsapp') || questionText.includes('celular')
            ? 3
            : questionText === 'telefone' || questionText.includes('telefone pessoal')
              ? 2
              : 0;

        if (
          answer &&
          !isOtherPersonPhone &&
          phonePriority > extractedPhonePriority
        ) {
          extractedPhone = answer;
          extractedPhonePriority = phonePriority;
        }
        if (answer && (questionText.includes('nascimento') || questionText.includes('data de nasc') || questionText.includes('aniversario'))) {
          extractedBirthDate = answer;
        }
      });

      const requestData = {
        template_id: template.id,
        patient_name: patientData.name.trim(),
        patient_email: extractedEmail ? extractedEmail.trim() : null,
        patient_phone: extractedPhone || null,
        patient_birth_date: extractedBirthDate || null,
        answers: answers
      };

      // Usar rota pública sem autenticação
      const response = await fetch(`${backendUrl}/api/anamnese/public/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erro ao enviar respostas. Tente novamente.');
      }
    } catch (error) {
      setError(error.message || 'Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question, index) => {
    const value = responses[question.id] || '';

    const addressQuestion = isAddressQuestion(question);

    return (
      <div key={question.id} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 shadow-sm hover:shadow-md transition-all duration-200">
        <label className="block text-sm font-semibold text-gray-800 mb-4">
          <span className="inline-flex items-center">
            <span className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-bold px-3 py-1.5 rounded-full mr-3">
              {index + 1}
            </span>
            {question.question_text}
            {question.is_required && <span className="text-red-500 ml-2 text-base">*</span>}
          </span>
        </label>
        
        {question.question_type === 'text' && (
          addressQuestion ? (
            <AddressCepField
              value={value}
              required={question.is_required}
              onChange={(formattedAddress) =>
                handleResponseChange(
                  question.id,
                  formattedAddress,
                  question.question_text
                )
              }
            />
          ) : (
            <input
              type="text"
              value={value}
              onChange={(e) => {
                if (
                  question.question_text
                    .toLowerCase()
                    .includes('telefone')
                ) {
                  handlePatientDataChange('phone', e.target.value);
                  handleResponseChange(
                    question.id,
                    e.target.value,
                    question.question_text
                  );
                } else {
                  handleResponseChange(
                    question.id,
                    e.target.value,
                    question.question_text
                  );
                }
              }}
              required={question.is_required}
              className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-inner"
              placeholder={
                question.question_text.toLowerCase().includes('telefone') ||
                question.question_text.toLowerCase().includes('celular') ||
                question.question_text.toLowerCase().includes('whatsapp')
                  ? '(00) 00000-0000'
                  : question.question_text.toLowerCase().includes('cpf')
                    ? '000.000.000-00'
                    : 'Digite sua resposta...'
              }
            />
          )
        )}
        
        {question.question_type === 'textarea' && (
          <textarea
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value, question.question_text)}
            required={question.is_required}
            rows={4}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-inner resize-vertical"
            placeholder="Digite sua resposta detalhada..."
          />
        )}

        {question.question_type === 'date' && (
          <div className="relative">
            <Calendar className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" />
            <input
              type="date"
              value={value}
              onChange={(e) => handleResponseChange(question.id, e.target.value, question.question_text)}
              required={question.is_required}
              className="w-full pl-12 pr-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-inner"
            />
          </div>
        )}
        
        {question.question_type === 'radio' && (
          <div className="space-y-3">
            {question.options?.map((option, optIndex) => (
              <label key={optIndex} className="flex items-center p-4 bg-white rounded-xl border-2 border-blue-100 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all duration-200 group">
                <input
                  type="radio"
                  name={question.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleResponseChange(question.id, e.target.value, question.question_text)}
                  required={question.is_required}
                  className="mr-4 text-blue-600 focus:ring-blue-500 w-5 h-5"
                />
                <span className="text-gray-700 font-medium group-hover:text-blue-700">{option}</span>
              </label>
            ))}
          </div>
        )}
        
        {question.question_type === 'checkbox' && (
          <div className="space-y-3">
            {question.options?.map((option, optIndex) => (
              <label key={optIndex} className="flex items-center p-4 bg-white rounded-xl border-2 border-blue-100 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all duration-200 group">
                <input
                  type="checkbox"
                  value={option}
                  checked={(Array.isArray(value) ? value : []).includes(option)}
                  onChange={(e) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    if (e.target.checked) {
                      handleResponseChange(question.id, [...currentValues, option], question.question_text);
                    } else {
                      handleResponseChange(question.id, currentValues.filter(v => v !== option), question.question_text);
                    }
                  }}
                  className="mr-4 text-blue-600 focus:ring-blue-500 w-5 h-5"
                />
                <span className="text-gray-700 font-medium group-hover:text-blue-700">{option}</span>
              </label>
            ))}
          </div>
        )}

        {question.question_type === 'select' && (
          <select
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value, question.question_text)}
            required={question.is_required}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-inner"
          >
            <option value="">Selecione...</option>
            {(question.options || []).map((option, optIndex) => (
              <option key={optIndex} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}

        {question.question_type === 'number' && (
          <input
            type="number"
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value, question.question_text)}
            required={question.is_required}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-inner"
            placeholder="Digite um número"
          />
        )}

        {question.question_type === 'email' && (
          <input
            type="email"
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value, question.question_text)}
            required={question.is_required}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-inner"
            placeholder="email@exemplo.com"
          />
        )}

        {question.question_type === 'phone' && (
          <input
            type="text"
            value={value}
            onChange={(e) => handleResponseChange(question.id, formatPhone(e.target.value), question.question_text)}
            required={question.is_required}
            className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-inner"
            placeholder="(00) 00000-0000"
            maxLength={15}
          />
        )}

      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-xl p-12 max-w-md">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-6 font-semibold text-lg">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  if (error && !template) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-xl p-12 max-w-md">
          <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops! Algo deu errado</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-8 py-3 rounded-xl hover:bg-red-700 transition-colors font-semibold"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-xl p-12 max-w-md">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Obrigado!</h2>
          <p className="text-gray-600 mb-2 text-lg">Suas respostas foram enviadas com sucesso.</p>
          {clinicInfo?.clinic_name && (
            <p className="text-sm text-gray-500 mt-4">
              {clinicInfo.clinic_name} entrará em contato em breve.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header da Clínica */}
        {clinicInfo && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                {clinicLogoSrc ? (
                  <img
                    src={clinicLogoSrc}
                    alt="Logo da Clínica"
                    className="w-20 h-20 object-contain rounded-xl border-2 border-blue-200 bg-blue-50 p-2"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                    <Building className="w-10 h-10 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {clinicInfo.clinic_name || 'Clínica Odontológica'}
                  </h1>
                  <div className="flex items-center space-x-6 text-sm text-gray-600">
                    {clinicInfo.phone && (
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 mr-2" />
                        {clinicInfo.phone}
                      </div>
                    )}
                    {clinicInfo.address && (
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {clinicInfo.address}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-400 mb-1">Powered by</div>
                <div className="text-sm font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">OdontoAssist AI</div>
              </div>
            </div>
          </div>
        )}

        {/* Formulário */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-blue-100">
          {/* Cabeçalho do Formulário */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8">
            <div className="flex items-center">
              <FileText className="w-10 h-10 mr-4" />
              <div>
                <h2 className="text-3xl font-bold mb-2">
                  Anamnese Odontológica
                </h2>
                <p className="text-blue-100 text-lg">
                  Preencha com atenção as informações solicitadas por{' '}
                  {clinicInfo?.clinic_name || 'sua clínica odontológica'}.
                </p>
                <div className="mt-6">
                  <div className="flex justify-between text-sm text-blue-100 mb-2">
                    <span>
                      Progresso do preenchimento
                    </span>
                    <span>
                      {answeredQuestions} de {totalQuestions} perguntas respondidas ({progressPercent}%)
                    </span>
                  </div>

                  <div className="w-full bg-white/25 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-white h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>                
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8">
            {error && (
              <div className="mb-8 p-6 bg-red-50 border-2 border-red-200 rounded-xl flex items-start">
                <AlertCircle className="w-6 h-6 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-700 font-semibold text-lg">Atenção!</p>
                  <p className="text-red-600">{error}</p>
                </div>
              </div>
            )}

            {/* Dados do Paciente */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <User className="w-6 h-6 mr-3 text-blue-600" />
                Identificação
              </h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  value={patientData.name}
                  onChange={(e) => handlePatientDataChange('name', e.target.value)}
                  required
                  className="w-full px-4 py-4 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white shadow-inner"
                  placeholder="Seu nome completo"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Os demais dados serão coletados nas perguntas abaixo
                </p>
              </div>
            </div>

            {/* Perguntas */}
            <div className="space-y-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <FileText className="w-6 h-6 mr-3 text-blue-600" />
                Questionário ({template?.questions?.length || 0} perguntas)
              </h3>
              
              {template?.questions?.map((question, index) => renderQuestion(question, index))}
            </div>

            {/* Botão de Envio */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-5 px-8 rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {submitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                    Enviando Respostas...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 mr-3" />
                    Enviar Respostas
                  </div>
                )}
              </button>
              
              <p className="text-center text-sm text-gray-500 mt-4">
                Suas informações são seguras e confidenciais 🔒
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© 2024 {clinicInfo?.clinic_name || 'Clínica'} - Todos os direitos reservados</p>
          <p className="mt-1">Sistema desenvolvido com ❤️ pela OdontoAssist AI</p>
        </div>
      </div>
    </div>
  );
};

export default PublicAnamnesePage;
